import { shortName, type CheckIssue, type IsoDate } from '@kancelaria/shared';
import { useMemo, type ReactNode } from 'react';
import { useData } from '../../store/data.js';
import type { CellIssue } from '../Grid.js';
import { Modal } from '../ui.js';

/** Остання перевірка місяця: що повернув сервер і коли. */
export interface CheckResult {
  issues: CheckIssue[];
  at: Date;
}

/**
 * З чим саме розбіжність у клітинці. Рапорт порівнюємо з таблицею (не
 * видавали — те саме, що «ні»); розхід — з таблицею, лише якщо на цю дату його
 * подавали.
 */
export function issueOf(i: CheckIssue): CellIssue {
  const report = i.report !== 'unknown' && (i.report ?? false) !== i.table;
  const expense = i.expense !== null && i.expense !== i.table;
  return { report, expense, title: issueText(i) };
}

const yn = (v: boolean | null | 'unknown', none: string): string =>
  v === 'unknown' ? 'невідомо' : v === null ? none : v ? 'так' : 'ні';

function issueText(i: CheckIssue): string {
  return [
    `Рапорт: ${yn(i.report, 'не видавали')}${i.issuedAt ? ` (${i.issuedAt.slice(0, 16)} UTC)` : ''}`,
    `Таблиця: ${i.table ? 'компенсація' : 'ні'}`,
    `Розхід: ${yn(i.expense, 'не подавали')}`,
  ].join('\n');
}

/** Для таблиці: людина → `date|meal` → розбіжність. */
export function checkMap(issues: CheckIssue[]): Map<string, Map<string, CellIssue>> {
  const map = new Map<string, Map<string, CellIssue>>();
  for (const i of issues) {
    let row = map.get(i.personId);
    if (!row) map.set(i.personId, (row = new Map()));
    row.set(`${i.date}|${i.meal}`, issueOf(i));
  }
  return map;
}

const dm = (d: IsoDate): string => `${d.slice(8)}.${d.slice(5, 7)}`;

/**
 * «Перевірка» місяця: виданий рапорт, таблиця й поданий розхід. Звідси
 * запускають звірку, бачать підсумок і перелік розбіжностей по людях, а
 * клацнувши рядок — переходять до клітинки в таблиці.
 */
export function CheckModal(props: {
  from: IsoDate;
  to: IsoDate;
  monthLabel: string;
  result: CheckResult | null;
  busy: boolean;
  highlighted: boolean;
  onRun: () => void;
  onHighlight: (on: boolean) => void;
  onShow: (personId: string, date: IsoDate, meal: number) => void;
  onClose: () => void;
}): ReactNode {
  const { result } = props;
  const people = useData(s => s.people);
  const meals = useData(s => s.session!.org.settings.meals);

  const summary = useMemo(() => {
    if (!result) return null;
    let report = 0;
    let expense = 0;
    const byPerson = new Map<string, Array<CheckIssue & { cell: CellIssue }>>();
    for (const i of result.issues) {
      const cell = issueOf(i);
      if (cell.report) report++;
      if (cell.expense) expense++;
      const list = byPerson.get(i.personId) ?? [];
      list.push({ ...i, cell });
      byPerson.set(i.personId, list);
    }
    const order = new Map(people.map((p, n) => [p.id, n]));
    const groups = [...byPerson]
      .map(([id, list]) => ({ person: people.find(p => p.id === id), id, list }))
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    return { report, expense, groups };
  }, [result, people]);

  return (
    <Modal
      title="Перевірка"
      onClose={props.onClose}
      width={620}
      footer={
        <div className="btn-row">
          {result?.issues.length ? (
            <button className="btn" onClick={() => props.onHighlight(!props.highlighted)}>
              {props.highlighted ? 'Сховати в таблиці' : 'Показати в таблиці'}
            </button>
          ) : null}
          <a className="btn" href={`/api/documents/audit.csv?from=${props.from}&to=${props.to}`} download>
            CSV
          </a>
          <button className="btn primary" disabled={props.busy} onClick={props.onRun}>
            {props.busy ? 'Перевіряю…' : result ? 'Перевірити знову' : 'Запустити перевірку'}
          </button>
        </div>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        {props.monthLabel}: виданий рапорт, таблиця й поданий розхід — по кожному прийому їжі.
        Рапорти, видані до журналу клітинок, не звіряються.
      </p>

      {!summary ? (
        <div className="note">Натисніть «Запустити перевірку».</div>
      ) : !result!.issues.length ? (
        <div className="note">
          Розбіжностей немає. Перевірено о {result!.at.toLocaleTimeString('uk', { hour: '2-digit', minute: '2-digit' })}.
        </div>
      ) : (
        <>
          <div className="btn-row" style={{ marginBottom: 12 }}>
            <span className="pill">Усього: {result!.issues.length}</span>
            <span className="pill check-report">З рапортом: {summary.report}</span>
            <span className="pill check-expense">З розходом: {summary.expense}</span>
            <span className="pill">Людей: {summary.groups.length}</span>
            <span className="muted">
              о {result!.at.toLocaleTimeString('uk', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {summary.groups.map(g => (
            <section key={g.id} style={{ marginBottom: 12 }}>
              <h4 style={{ margin: '0 0 4px' }}>
                {g.person ? shortName(g.person) : g.id} <span className="pill">{g.list.length}</span>
              </h4>
              {g.list.map(i => (
                <button
                  key={`${i.date}|${i.meal}`}
                  className="check-row"
                  title="Показати в таблиці"
                  onClick={() => props.onShow(i.personId, i.date, i.meal)}
                >
                  <span className={`check-mark${i.cell.report ? ' report' : ''}${i.cell.expense ? ' expense' : ''}`} />
                  <span className="check-when">
                    {dm(i.date)} {meals[i.meal]?.name ?? i.meal}
                  </span>
                  <span className="muted">
                    рапорт {yn(i.report, '—')} · таблиця {i.table ? 'к' : '—'} · розхід {yn(i.expense, '—')}
                  </span>
                </button>
              ))}
            </section>
          ))}
        </>
      )}
    </Modal>
  );
}
